import { useNavigate } from "react-router-dom";
import { useState, useCallback, useMemo, useEffect } from "react";
import '../css/Empresas.css';
import { ReactComponent as Eliminar } from '../assets/icon/eliminar.svg';
import { ReactComponent as Editar } from '../assets/icon/editar.svg';
import { ReactComponent as Agregar } from '../assets/icon/agregarEmpresa.svg';
import { ReactComponent as Recargar } from '../assets/icon/recargar.svg';
import TablaEmpresas from "../Empresas/TablaEmpresas";
import { useEmpresas, deleteEmpresa } from '../services/empresas';
import { useToast } from '../contexts/ToastContext';
import BulkDeleteModal from '../Components/BulkDeleteModal';

function Empresas() {
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingItems, setDeletingItems] = useState([]);
  const { showError, showSuccess } = useToast();

  const { data: empresas, loading, error, refetch } = useEmpresas({
    immediate: true
  });

  useEffect(() => {
    if (error) {
      showError(error, 'Error de conexión');
    }
  }, [error, showError]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.length === 0) return;
    
    const itemsToDelete = empresas.filter(e => selectedIds.includes(e.IdEmpresa));
    setDeletingItems(itemsToDelete);
    setShowDeleteModal(true);
  }, [selectedIds, empresas]);

  const handleConfirmDelete = useCallback(async (id, item) => {
    const result = await deleteEmpresa(id);
    if (result.success && result.error === 0) {
      return { success: true };
    }
    throw new Error(result.mensaje || 'Error al eliminar');
  }, []);

  const handleDeleteComplete = useCallback(() => {
    setShowDeleteModal(false);
    setSelectedIds([]);
    refetch();
    showSuccess('Empresa(s) eliminada(s) correctamente');
  }, [refetch, showSuccess]);

  const handleEditSelected = useCallback(() => {
    if (selectedIds.length === 1) {
      navigate(`/dashboard/empresas/editarEmpresa/${selectedIds[0]}`);
    }
  }, [selectedIds, navigate]);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const buttonStates = useMemo(() => {
    const hasNoSelection = selectedIds.length === 0;
    const hasSingleSelection = selectedIds.length === 1;
    const hasMultipleSelection = selectedIds.length > 1;
    const hasData = empresas && empresas.length > 0;

    return {
      nuevo: hasNoSelection && hasData,
      editar: hasSingleSelection && hasData,
      eliminar: (hasSingleSelection || hasMultipleSelection) && hasData
    };
  }, [selectedIds, empresas]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <div className="bodyEmpresas">

      {/* TÍTULO */}
      <div className='tituloEmpresas'><p>Empresas</p></div>

      {/* CONTENIDO SUPERIOR */}
      <div className='contenidoSuperiorEmpresas'>
        <div className='botonesEmpresas'>
          <button
            className='botonesEdicionEmpresas'
            onClick={() => navigate("/dashboard/empresas/agregarempresa")}
            disabled={!buttonStates.nuevo}
            title={buttonStates.nuevo ? "Agregar nueva empresa" : "Deshabilitado cuando hay selección"}
          >
            <div className="svgBotonesEmpresas"><Agregar className="logo-svgBotones" /></div>
            Agregar Empresa
          </button>

          <button
	    id="botoneEdicionEmpresa"
            className='botonesEdicionEmpresas'
            disabled={!buttonStates.editar}
            onClick={handleEditSelected}
            title={buttonStates.editar ? "Editar empresa seleccionada" : "Selecciona una empresa para editar"}
          >
            <div className="svgBotonesEmpresas"><Editar className="logo-svgBotones" /></div>
            Editar Empresa
          </button>

          <button 
            className='botonesEdicionEmpresas'
            disabled={!buttonStates.eliminar}
            onClick={handleDeleteSelected}
            title={buttonStates.eliminar ? `Eliminar ${selectedIds.length} empresa(s)` : "Selecciona empresas para eliminar"}
          >
            <div className="svgBotonesEmpresas"><Eliminar className="logo-svgBotones" /></div>
            Eliminar Empresa{selectedIds.length > 1 ? 's' : ''}
          </button>

          <button 
            className='botonesEdicionEmpresas'
            onClick={handleRefresh}
            disabled={loading}
            title="Recargar empresas"
          >
            <div className="svgBotonesEmpresas">
              <Recargar className={`logo-svgBotones ${loading ? 'rotating' : ''}`} />
            </div>
            Recargar
          </button>
        </div>
      </div>

      {/* AQUÍ SE MOSTRARÁ LA TABLA */}
      <div className="tablaContainerEmpresas">
        <TablaEmpresas 
          empresas={empresas}
          loading={loading}
          error={error}
          onRetry={refetch}
          emptyStateNoReload={true}
          pageSize={20}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onSelectionChange={(selectedIdsList) => {
            setSelectedIds(selectedIdsList || []);
          }}
        />
      </div>

      <BulkDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Eliminar Empresas"
        items={deletingItems}
        onDelete={handleConfirmDelete}
        getItemId={(item) => item.IdEmpresa}
        getItemLabel={(item) => item.Nombre || `Empresa #${item.IdEmpresa}`}
        entityName="empresa"
      />

    </div>
  );
}

export default Empresas;
